import { ApiClient } from "../apps/api-client";
import type {
  EmitGameEventResponse,
  GameEventKey,
} from "../apps/LooplaySDK.types";
import type {
  EmitGameEventOptions,
  LooplayIframeAuthListener,
  LooplayIframeAuthOptions,
  LooplayIframeAuthState,
  RequestAdsOptions,
  TrackMatchOptions,
} from "./types";

const DEFAULT_ANON_ID_STORAGE_KEY = "looplay:anon-id";
const DEFAULT_ANONYMOUS_FALLBACK_TIMEOUT_MS = 4000;
const DEFAULT_REQUEST_ADS_TIMEOUT_MS = 5000;

const INITIAL_STATE: LooplayIframeAuthState = {
  jwt: null,
  gameId: null,
  apiUrl: null,
  anonymousId: null,
  origin: null,
  receivedAt: null,
};

type LooplayIframeAuthMessage =
  | {
      type: "LOOPLAY_AUTH";
      jwt?: unknown;
      gameId?: unknown;
      apiUrl?: unknown;
    }
  | {
      type: "LOOPLAY_ADS_WATCHED";
      requestId?: unknown;
    }
  | {
      type: "LOOPLAY_ADS_UNAVAILABLE";
      requestId?: unknown;
    };

const isRecord = (value: unknown): value is Record<string, unknown> =>
  typeof value === "object" && value !== null;

const toOptionalString = (value: unknown): string | null => {
  if (typeof value !== "string") return null;
  const normalized = value.trim();
  return normalized ? normalized : null;
};

function generateAnonymousId(): string {
  if (
    typeof crypto !== "undefined" &&
    typeof crypto.randomUUID === "function"
  ) {
    return crypto.randomUUID();
  }

  // Fallback UUID v4 for environments without crypto.randomUUID (older
  // browsers, non-secure contexts). Not cryptographically strong, but only
  // used as an anonymous, non-sensitive device identifier.
  return "xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx".replace(/[xy]/g, (c) => {
    const r = (Math.random() * 16) | 0;
    const v = c === "x" ? r : (r & 0x3) | 0x8;
    return v.toString(16);
  });
}

function readStoredAnonymousId(key: string): string | null {
  if (typeof window === "undefined" || !window.localStorage) return null;
  try {
    return window.localStorage.getItem(key);
  } catch {
    return null;
  }
}

function writeStoredAnonymousId(key: string, value: string): void {
  if (typeof window === "undefined" || !window.localStorage) return;
  try {
    window.localStorage.setItem(key, value);
  } catch {
    // Private browsing, storage full, or blocked — the id just won't
    // persist across reloads; tracking for this session still works.
  }
}

/**
 * Handles auth for games that may run in two contexts:
 *
 * 1. Embedded as an iframe inside the Looplay web app — the host page
 *    pushes `{ type: 'LOOPLAY_AUTH', jwt?, gameId, apiUrl }` via postMessage
 *    in response to a `LOOPLAY_AUTH_REQUEST`. Tracking calls use the real
 *    user's bearer token when available, otherwise fall back to anonymous
 *    tracking (analytics-only, not eligible for quest/reward).
 * 2. Standalone — published anywhere else, or the parent never responds.
 *    Falls back to an anonymous, locally-generated device id. Tracking
 *    still works (the backend records it for analytics), but never feeds
 *    the quest/reward engine.
 *
 * Either way, `appId` (issued to the creator after registering the game)
 * is required to track — see `LooplayIframeAuthOptions.appId`.
 */
export class LooplayIframeAuth {
  private state: LooplayIframeAuthState = INITIAL_STATE;
  private initialized = false;
  private lifecycleTrackingInitialized = false;
  private lastGameStartedKey: string | null = null;
  private readonly integrationDetectionKeys = new Set<string>();
  private client: ApiClient | null = null;
  private readonly listeners = new Set<LooplayIframeAuthListener>();
  private readonly options: LooplayIframeAuthOptions;
  private messageListener: ((event: MessageEvent<unknown>) => void) | null =
    null;
  private anonymousFallbackTimer: ReturnType<typeof setTimeout> | null = null;
  private pendingAdsRequests = new Map<
    string,
    {
      resolve: (value: boolean) => void;
      timer: ReturnType<typeof setTimeout> | null;
    }
  >();
  private requestAdsSequence = 0;
  private adsRequestInFlight = false;
  private adsRequestsSent = 0;
  private lastAdsRequestAt = 0;
  private autoAdsActionCount = 0;

  constructor(options: LooplayIframeAuthOptions = {}) {
    this.options = options;
  }

  getState(): LooplayIframeAuthState {
    return this.state;
  }

  getJwt(): string | null {
    return this.state.jwt;
  }

  getApiUrl(): string | null {
    return this.state.apiUrl ?? this.options.apiUrl ?? null;
  }

  /** Resolves to the configured `appId`, falling back to whatever the parent pushed as `gameId`. */
  getGameId(): string | null {
    return this.state.gameId ?? this.options.appId ?? null;
  }

  getAnonymousId(): string | null {
    return this.state.anonymousId;
  }

  /** True once authenticated (bearer) or anonymous tracking is ready, given a configured `apiUrl`/`appId`. */
  canTrack(): boolean {
    if (!this.getApiUrl() || !this.getGameId()) return false;
    return Boolean(this.state.jwt || this.state.anonymousId);
  }

  subscribe(listener: LooplayIframeAuthListener): () => void {
    this.listeners.add(listener);
    listener(this.state);
    return () => this.listeners.delete(listener);
  }

  requestAuth(): void {
    if (typeof window === "undefined" || window.parent === window) {
      this.debug("standalone mode detected; auth request skipped");
      return;
    }

    this.debug("requesting auth from parent", {
      targetOrigin: this.resolveParentOrigin() ?? "*",
    });
    window.parent.postMessage(
      { type: "LOOPLAY_AUTH_REQUEST" },
      this.resolveParentOrigin() ?? "*"
    );
  }

  /**
   * Requests the parent host to show a rewarded ad.
   * In a Telegram Mini App setup, the game sends this request from the iframe
   * and the parent page (the Mini App shell) bridges it to Adsgram, then
   * responds with `LOOPLAY_ADS_WATCHED` after the ad finishes.
   */
  requestAds(options: RequestAdsOptions = {}): Promise<boolean> {
    if (typeof window === "undefined" || window.parent === window) {
      this.debug("requestAds skipped; standalone mode detected");
      return Promise.resolve(false);
    }

    if (this.adsRequestInFlight) {
      this.debug("requestAds skipped; another ad request is already in flight");
      return Promise.resolve(false);
    }

    const parentOrigin = this.resolveParentOrigin() ?? "*";
    const requestId = `ads-${Date.now()}-${++this.requestAdsSequence}`;
    const timeoutMs = options.timeoutMs ?? DEFAULT_REQUEST_ADS_TIMEOUT_MS;

    this.adsRequestInFlight = true;
    this.adsRequestsSent += 1;
    this.lastAdsRequestAt = Date.now();
    this.debug("requesting rewarded ad from parent", {
      requestId,
      targetOrigin: parentOrigin,
      gameId: this.getGameId(),
    });

    return new Promise<boolean>((resolve) => {
      const timer = setTimeout(() => {
        this.pendingAdsRequests.delete(requestId);
        this.adsRequestInFlight = false;
        this.debug("requestAds timed out", { requestId, timeoutMs });
        resolve(false);
      }, timeoutMs);

      this.pendingAdsRequests.set(requestId, { resolve, timer });

      window.parent.postMessage(
        {
          type: "LOOPLAY_REQUEST_ADS",
          requestId,
          gameId: this.getGameId(),
        },
        parentOrigin
      );
    });
  }

  /** Idempotent; attaches the postMessage listener (if embedded) and arms the anonymous fallback. */
  init(): void {
    if (this.initialized || typeof window === "undefined") return;
    this.initialized = true;

    // When appId/apiUrl are supplied by the game (for example via env), the
    // integration can be detected immediately. If either value comes from
    // the parent, setState() below will retry once the auth message arrives.
    void this.detectIntegrationOnce();

    const isEmbedded = window.parent !== window;
    this.debug("auth listener initialized", {
      isIframe: isEmbedded,
      parentOrigin: this.resolveParentOrigin(),
    });

    if (!isEmbedded) {
      // No parent frame to push auth at all — go anonymous right away.
      this.activateAnonymousMode();
      return;
    }

    const parentOrigin = this.resolveParentOrigin();

    if (!parentOrigin) {
      // `document.referrer` can be empty (Referrer-Policy, browser privacy
      // settings, extensions), which silently disables the origin check
      // below and lets any window that is literally `window.parent` push
      // auth. Pass `parentOrigin` explicitly to keep this check effective.
      console.warn(
        "[LooplaySDK] LooplayIframeAuth could not resolve a parent origin to verify postMessage " +
          "against (document.referrer is empty). Auth messages from window.parent will be accepted " +
          "regardless of origin. Pass `parentOrigin` explicitly to harden this."
      );
    }

    this.messageListener = (event: MessageEvent<unknown>) => {
      if (event.source !== window.parent) return;
      if (parentOrigin && event.origin !== parentOrigin) {
        this.debug("ignored message from unexpected origin", {
          expectedOrigin: parentOrigin,
          receivedOrigin: event.origin,
        });
        return;
      }

      this.handleIncomingMessage(event.data, event.origin);
    };

    window.addEventListener("message", this.messageListener);

    // The host page might be an older protocol version, slow, or simply not
    // respond — don't leave the game untrackable forever.
    this.anonymousFallbackTimer = setTimeout(() => {
      if (this.state.jwt) return;
      this.activateAnonymousMode();
    }, this.options.anonymousFallbackTimeoutMs ?? DEFAULT_ANONYMOUS_FALLBACK_TIMEOUT_MS);

    this.requestAuth();
  }

  /** Removes the postMessage listener, cancels timers, and clears subscribers; safe to call multiple times. */
  dispose(): void {
    if (typeof window !== "undefined" && this.messageListener) {
      window.removeEventListener("message", this.messageListener);
    }
    if (this.anonymousFallbackTimer !== null) {
      clearTimeout(this.anonymousFallbackTimer);
    }
    for (const pending of this.pendingAdsRequests.values()) {
      if (pending.timer !== null) clearTimeout(pending.timer);
      pending.resolve(false);
    }
    this.pendingAdsRequests.clear();

    this.messageListener = null;
    this.anonymousFallbackTimer = null;
    this.initialized = false;
    this.lifecycleTrackingInitialized = false;
    this.integrationDetectionKeys.clear();
    this.listeners.clear();
  }

  /** Lazily builds (and rebuilds on auth change) the `ApiClient` bound to the current auth mode. */
  getClient(): ApiClient {
    if (this.client) return this.client;

    const baseUrl = this.getApiUrl();

    this.debug("creating sdk ApiClient", { baseUrl });
    this.client = new ApiClient({
      baseUrl: baseUrl ?? undefined,
      getAccessToken: async () => this.getJwt() ?? undefined,
      getAnonymousId: () => this.getAnonymousId() ?? undefined,
    });

    return this.client;
  }

  /** Call once when the game view opens, before any play/match tracking. */
  trackView(): Promise<boolean> {
    const gameId = this.getGameId();
    if (!this.canTrack() || !gameId) {
      this.debug("trackView skipped; auth is not ready", {
        hasGameId: Boolean(gameId),
      });
      return Promise.resolve(false);
    }

    this.debug("trackView dispatched", { gameId });
    return this.getClient().trackView(gameId);
  }

  /** Starts a new play session; call when the user presses Play. Returns the play session id. */
  startPlaySession(): string {
    return this.getClient().startPlaySession();
  }

  trackPlay(playTimeSeconds?: number): Promise<boolean> {
    const gameId = this.getGameId();
    if (!this.canTrack() || !gameId) {
      this.debug("trackPlay skipped; auth is not ready", {
        hasGameId: Boolean(gameId),
      });
      return Promise.resolve(false);
    }

    this.debug("trackPlay dispatched", { gameId, playTimeSeconds });
    return this.getClient().trackPlay(gameId, playTimeSeconds);
  }

  trackMatch(matchId: string, opts: TrackMatchOptions): Promise<boolean> {
    const gameId = this.getGameId();
    if (!this.canTrack() || !gameId) {
      this.debug("trackMatch skipped; auth is not ready", { matchId });
      return Promise.resolve(false);
    }

    this.debug("trackMatch dispatched", { gameId, matchId, ...opts });
    const result = this.getClient().trackMatch(gameId, {
      matchId,
      matchDurationSeconds: opts.durationSeconds,
      isCompleted: opts.isCompleted,
      isWin: opts.isWin,
    });
    void result
      .then((ok) => {
        if (ok) this.recordAutoRequestAdsActivity();
      })
      .catch(() => undefined);
    return result;
  }

  emitGameEvent(
    actionCode: GameEventKey | string,
    opts?: EmitGameEventOptions
  ): Promise<EmitGameEventResponse | null> {
    const gameId = this.getGameId();
    if (!this.canTrack() || !gameId) {
      this.debug("emitGameEvent skipped; auth is not ready", { actionCode });
      return Promise.resolve(null);
    }

    this.debug("emitGameEvent dispatched", { gameId, actionCode, ...opts });
    return this.getClient().emit(gameId, actionCode, opts);
  }

  /** Idempotent; auto-emits GAME_STARTED exactly once per distinct auth session (bearer or anonymous). */
  initLifecycleTracking(): void {
    if (this.lifecycleTrackingInitialized || typeof window === "undefined")
      return;
    this.lifecycleTrackingInitialized = true;
    this.debug("lifecycle tracking initialized");

    this.subscribe(() => {
      const key = this.getTrackingKey();
      if (!key || this.lastGameStartedKey === key) return;

      this.lastGameStartedKey = key;
      this.debug("auto GAME_STARTED dispatch");
      void this.emitGameEvent("GAME_STARTED");
    });
  }

  private getTrackingKey(): string | null {
    const gameId = this.getGameId();
    const apiUrl = this.getApiUrl();
    const identity = this.state.jwt ?? this.state.anonymousId;
    if (!gameId || !apiUrl || !identity) return null;
    return `${apiUrl}\n${gameId}\n${identity}`;
  }

  /** Generates (or restores) a persisted anonymous device id and activates anonymous tracking. */
  private activateAnonymousMode(): void {
    if (this.state.anonymousId) return;

    const key =
      this.options.anonymousIdStorageKey ?? DEFAULT_ANON_ID_STORAGE_KEY;
    const anonymousId = readStoredAnonymousId(key) ?? generateAnonymousId();
    writeStoredAnonymousId(key, anonymousId);

    this.lastGameStartedKey = null;
    this.setState({ ...this.state, anonymousId, receivedAt: Date.now() });
    this.debug("anonymous tracking mode active", { anonymousId });
  }

  private handleAuthMessage(
    data: Record<string, unknown>,
    origin: string
  ): void {
    if (this.anonymousFallbackTimer !== null) {
      clearTimeout(this.anonymousFallbackTimer);
      this.anonymousFallbackTimer = null;
    }

    const jwt = toOptionalString(data.jwt);
    const gameId = toOptionalString(data.gameId);
    const apiUrl = toOptionalString(data.apiUrl);
    const resolvedGameId = gameId ?? this.getGameId();
    const resolvedApiUrl = apiUrl ?? this.getApiUrl();

    if (!resolvedGameId || !resolvedApiUrl) {
      this.lastGameStartedKey = null;
      this.setState({
        ...this.state,
        jwt: null,
        gameId: null,
        apiUrl: null,
        origin,
        receivedAt: Date.now(),
      });
      this.debug(
        "auth message missing game identity; falling back to anonymous tracking"
      );
      this.activateAnonymousMode();
      return;
    }

    if (!jwt) {
      this.lastGameStartedKey = null;
      this.setState({
        ...this.state,
        jwt: null,
        gameId: resolvedGameId,
        apiUrl: resolvedApiUrl,
        origin,
        receivedAt: Date.now(),
      });
      this.debug(
        "auth message missing jwt; falling back to anonymous tracking",
        {
          gameId: resolvedGameId,
          apiUrl: resolvedApiUrl,
        }
      );
      this.activateAnonymousMode();
      return;
    }

    this.lastGameStartedKey = null;
    this.setState({
      jwt,
      gameId,
      apiUrl,
      anonymousId: null,
      origin,
      receivedAt: Date.now(),
    });
  }

  private handleRequestAdsResult(data: LooplayIframeAuthMessage): void {
    if (data.type !== "LOOPLAY_ADS_WATCHED") return;

    const requestId = toOptionalString(data.requestId);
    if (!requestId) return;

    const pending = this.pendingAdsRequests.get(requestId);
    if (!pending) return;

    this.pendingAdsRequests.delete(requestId);
    if (pending.timer !== null) clearTimeout(pending.timer);

    this.adsRequestInFlight = false;
    this.debug("requestAds watched message received", { requestId });
    // TODO: call API to grant reward after the rewarded ad has been watched.
    pending.resolve(true);
  }

  private handleRequestAdsUnavailable(data: LooplayIframeAuthMessage): void {
    if (data.type !== "LOOPLAY_ADS_UNAVAILABLE") return;

    const requestId = toOptionalString(data.requestId);
    if (!requestId) return;

    const pending = this.pendingAdsRequests.get(requestId);
    if (!pending) return;

    this.pendingAdsRequests.delete(requestId);
    if (pending.timer !== null) clearTimeout(pending.timer);

    this.adsRequestInFlight = false;
    this.debug("requestAds unavailable message received", { requestId });
    pending.resolve(false);
  }

  private handleIncomingMessage(data: unknown, origin: string): void {
    if (!isRecord(data)) return;

    if (data.type === "LOOPLAY_AUTH") {
      this.handleAuthMessage(data, origin);
      return;
    }

    if (data.type === "LOOPLAY_ADS_WATCHED") {
      this.handleRequestAdsResult(data as LooplayIframeAuthMessage);
      return;
    }

    if (data.type === "LOOPLAY_ADS_UNAVAILABLE") {
      this.handleRequestAdsUnavailable(data as LooplayIframeAuthMessage);
      return;
    }
  }

  private setState(next: LooplayIframeAuthState): void {
    this.state = next;
    this.client = null;
    this.debug("auth state updated", {
      gameId: this.getGameId(),
      apiUrl: this.getApiUrl(),
      origin: next.origin,
      hasJwt: Boolean(next.jwt),
      hasAnonymousId: Boolean(next.anonymousId),
    });
    this.listeners.forEach((listener) => listener(next));
    void this.detectIntegrationOnce();
  }

  /** Detects each resolved game/API pair at most once for this iframe session. */
  private async detectIntegrationOnce(): Promise<void> {
    const gameId = this.getGameId();
    const apiUrl = this.getApiUrl();
    if (!gameId || !apiUrl) return;

    const key = `${apiUrl}\n${gameId}`;
    if (this.integrationDetectionKeys.has(key)) return;
    this.integrationDetectionKeys.add(key);

    try {
      await this.getClient().detectIntegration(gameId);
      this.debug("integration detected", { gameId, apiUrl });
    } catch (error) {
      // Detection is a heartbeat and must not prevent auth/tracking from
      // working. Remove the key so a later auth/config update can retry it.
      this.integrationDetectionKeys.delete(key);
      this.debug("integration detection failed", {
        gameId,
        apiUrl,
        error: error instanceof Error ? error.message : String(error),
      });
    }
  }

  private resolveParentOrigin(): string | null {
    if (this.options.parentOrigin) return this.options.parentOrigin;
    if (typeof document === "undefined" || !document.referrer) return null;

    try {
      return new URL(document.referrer).origin;
    } catch {
      return null;
    }
  }

  private debug(message: string, details?: Record<string, unknown>): void {
    this.options.onDebug?.(message, details);
  }

  private recordAutoRequestAdsActivity(): void {
    this.autoAdsActionCount += 1;

    const config = this.options.autoRequestAds;
    if (!config?.enabled) return;
    if (this.adsRequestInFlight) return;
    if (!this.canTrack()) return;

    const now = Date.now();
    if (config.cooldownMs && now - this.lastAdsRequestAt < config.cooldownMs) {
      return;
    }

    if (config.maxRequestsPerSession && this.adsRequestsSent >= config.maxRequestsPerSession) {
      return;
    }

    const threshold = config.afterActionCount ?? 0;
    const shouldRequest = threshold > 0 && this.autoAdsActionCount % threshold === 0;

    if (!shouldRequest) return;

    const chance = config.chance ?? 1;
    if (chance <= 0 || Math.random() > chance) {
      this.debug("auto requestAds skipped by chance", {
        actionCount: this.autoAdsActionCount,
        chance,
      });
      return;
    }

    this.debug("auto requestAds triggered", {
      actionCount: this.autoAdsActionCount,
    });

    void this.requestAds();
  }
}
